import axios from 'axios';
import { API_CONFIG, getProductUrl, buildApiUrl } from '../config/apiConfig';
import axiosInstance from './axiosInstance';
import { compressImageFile, compressImageFiles } from '../utils/compressImage';

export async function getProductsByCategory(categoryId: number) {
  const response = await axios.get(
    buildApiUrl(`${API_CONFIG.ENDPOINTS.PRODUCT.BASE}${API_CONFIG.ENDPOINTS.PRODUCT.GET_BY_CATEGORY}`, { CategoryId: categoryId })
  );
  return response.data;
}

export async function updateProduct(product: {
  ProductId: number;
  ProductName: string;
  ProductDescription: string;
  Price: number;
  QuantityAvailable: number;
  ImageUrls: string[];
  CategoryId?: number;
  IsMostSaleProduct?: boolean;
  VideoUrl?: string;
  CompanyName?: string;
  CompanyLogoUrl?: string;
  DisplayOrder?: number;
}) {
  if (product.CompanyName && product.CompanyName.trim()) {
    saveCustomCompanyLocal(product.CompanyName.trim(), product.CompanyLogoUrl);
  }
  const response = await fetch(getProductUrl(API_CONFIG.ENDPOINTS.PRODUCT.UPDATE), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(product),
  });
  return await response.json();
}

export async function deleteProduct(productId: number) {
  const response = await fetch(
    buildApiUrl(`${API_CONFIG.ENDPOINTS.PRODUCT.BASE}${API_CONFIG.ENDPOINTS.PRODUCT.DELETE}`, { ProductId: productId }),
    {
      method: 'DELETE',
    }
  );
  return await response.json();
}

export async function addProduct(product: {
  Name: string;
  Description: string;
  Price: number;
  QuantityAvailable: number;
  ImageUrls: string[];
  CategoryId: number;
  IsMostSaleProduct?: boolean;
  VideoUrl?: string;
  CompanyName?: string;
  CompanyLogoUrl?: string;
  DisplayOrder?: number;
}) {
  if (product.CompanyName && product.CompanyName.trim()) {
    saveCustomCompanyLocal(product.CompanyName.trim(), product.CompanyLogoUrl);
  }
  const response = await fetch(getProductUrl(API_CONFIG.ENDPOINTS.PRODUCT.ADD), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(product),
  });
  return await response.json();
}

export async function uploadProductImage(file: File, productId?: number) {
  const compressedFile = await compressImageFile(file);
  const formData = new FormData();
  formData.append('file', compressedFile);
  if (productId) formData.append('productId', String(productId));
  const response = await axiosInstance.post(
    getProductUrl('/UploadImage'),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data as { path: string; createdId?: number };
}

export async function uploadProductImages(files: File[], productId?: number) {
  const compressedFiles = await compressImageFiles(files);
  const formData = new FormData();
  for (const file of compressedFiles) {
    formData.append('files', file);
  }
  if (productId) formData.append('productId', String(productId));
  const response = await axiosInstance.post(
    getProductUrl('/UploadImages'),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data as { paths: string[]; createdIds?: number[] };
}

export async function getMostSaleProducts() {
  const response = await axiosInstance.get(
    getProductUrl('/GetMostSaleProducts')
  );
  return response.data;
}

const CUSTOM_COMPANIES_STORAGE_KEY = 'fc_custom_companies';
const CUSTOM_COMPANY_LOGOS_STORAGE_KEY = 'fc_company_logos';

export function getCompanyLogoMapLocal(): Record<string, string> {
  try {
    const raw = localStorage.getItem(CUSTOM_COMPANY_LOGOS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function getCompanyLogoLocal(companyName: string): string {
  if (!companyName || !companyName.trim()) return '';
  const map = getCompanyLogoMapLocal();
  const key = companyName.trim().toLowerCase();
  return map[key] || '';
}

export function saveCompanyLogoLocal(companyName: string, logoUrl?: string): void {
  const trimmedName = (companyName || '').trim();
  if (!trimmedName) return;
  const key = trimmedName.toLowerCase();
  const map = getCompanyLogoMapLocal();
  if (logoUrl && logoUrl.trim()) {
    map[key] = logoUrl.trim();
  } else if (logoUrl === '') {
    delete map[key];
  }
  try {
    localStorage.setItem(CUSTOM_COMPANY_LOGOS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore storage errors
  }
}

export function getCustomCompaniesLocal(): string[] {
  try {
    const raw = localStorage.getItem(CUSTOM_COMPANIES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveCustomCompanyLocal(companyName: string, companyLogoUrl?: string): string[] {
  const trimmed = companyName.trim();
  if (!trimmed) return getCustomCompaniesLocal();
  if (companyLogoUrl !== undefined) {
    saveCompanyLogoLocal(trimmed, companyLogoUrl);
  }
  const existing = getCustomCompaniesLocal();
  if (!existing.some(c => c.toLowerCase() === trimmed.toLowerCase())) {
    const updated = [...existing, trimmed].sort((a, b) => a.localeCompare(b));
    try {
      localStorage.setItem(CUSTOM_COMPANIES_STORAGE_KEY, JSON.stringify(updated));
    } catch {
      // ignore storage errors
    }
    return updated;
  }
  return existing;
}

export interface CompanySummary {
  companyName: string;
  companyLogoUrl?: string;
  productCount: number;
  sampleImageUrl?: string;
  categoryCount?: number;
}

export async function getAllCompanies(): Promise<CompanySummary[]> {
  try {
    const response = await axiosInstance.get(
      getProductUrl(API_CONFIG.ENDPOINTS.PRODUCT.GET_ALL_COMPANIES)
    );
    const data = Array.isArray(response.data) ? response.data : response.data?.value || [];
    return data.map((item: any) => {
      const cName = item.companyName || item.CompanyName || '';
      const apiLogo = item.companyLogoUrl || item.CompanyLogoUrl || '';
      if (cName && apiLogo) {
        saveCompanyLogoLocal(cName, apiLogo);
      }
      const effectiveLogo = apiLogo || getCompanyLogoLocal(cName) || undefined;
      return {
        companyName: cName,
        companyLogoUrl: effectiveLogo,
        productCount: Number(item.productCount ?? item.ProductCount ?? 0),
        sampleImageUrl: effectiveLogo || item.sampleImageUrl || item.SampleImageUrl || undefined,
        categoryCount: Number(item.categoryCount ?? item.CategoryCount ?? 0),
      };
    }).filter((c: CompanySummary) => Boolean(c.companyName));
  } catch {
    return [];
  }
}

export async function getProductsByCompany(companyName: string) {
  try {
    const response = await axios.get(
      buildApiUrl(`${API_CONFIG.ENDPOINTS.PRODUCT.BASE}${API_CONFIG.ENDPOINTS.PRODUCT.GET_BY_COMPANY}`, { CompanyName: companyName })
    );
    return Array.isArray(response.data) ? response.data : response.data?.value || [];
  } catch {
    return [];
  }
}

export async function renameCompany(
  oldCompanyName: string,
  newCompanyName: string,
  newCompanyLogoUrl?: string,
  updateLogo: boolean = false
) {
  const oldTrimmed = oldCompanyName.trim();
  const newTrimmed = newCompanyName.trim();
  if (!oldTrimmed || !newTrimmed) return;

  // Update local custom companies list
  const existing = getCustomCompaniesLocal().filter(
    c => c.toLowerCase() !== oldTrimmed.toLowerCase()
  );
  if (!existing.some(c => c.toLowerCase() === newTrimmed.toLowerCase())) {
    existing.push(newTrimmed);
  }
  existing.sort((a, b) => a.localeCompare(b));
  try {
    localStorage.setItem(CUSTOM_COMPANIES_STORAGE_KEY, JSON.stringify(existing));
  } catch {
    // ignore storage errors
  }

  // Update local company logo map
  const logoMap = getCompanyLogoMapLocal();
  const oldKey = oldTrimmed.toLowerCase();
  const newKey = newTrimmed.toLowerCase();
  const prevLogo = logoMap[oldKey];
  if (oldKey !== newKey) {
    delete logoMap[oldKey];
  }
  if (updateLogo) {
    if (newCompanyLogoUrl && newCompanyLogoUrl.trim()) {
      logoMap[newKey] = newCompanyLogoUrl.trim();
    } else {
      delete logoMap[newKey];
    }
  } else if (newCompanyLogoUrl && newCompanyLogoUrl.trim()) {
    logoMap[newKey] = newCompanyLogoUrl.trim();
  } else if (prevLogo) {
    logoMap[newKey] = prevLogo;
  }
  try {
    localStorage.setItem(CUSTOM_COMPANY_LOGOS_STORAGE_KEY, JSON.stringify(logoMap));
  } catch {
    // ignore storage errors
  }

  try {
    const response = await axiosInstance.put(
      getProductUrl(API_CONFIG.ENDPOINTS.PRODUCT.RENAME_COMPANY),
      {
        OldCompanyName: oldTrimmed,
        NewCompanyName: newTrimmed,
        NewCompanyLogoUrl: newCompanyLogoUrl?.trim() || null,
        UpdateLogo: updateLogo,
      }
    );
    return response.data;
  } catch {
    return null;
  }
}

export async function deleteCompany(companyName: string) {
  const targetTrimmed = companyName.trim();
  if (!targetTrimmed) return;

  // Remove from local custom companies list
  const existing = getCustomCompaniesLocal().filter(
    c => c.toLowerCase() !== targetTrimmed.toLowerCase()
  );
  try {
    localStorage.setItem(CUSTOM_COMPANIES_STORAGE_KEY, JSON.stringify(existing));
  } catch {
    // ignore storage errors
  }

  // Remove from local company logo map
  const logoMap = getCompanyLogoMapLocal();
  delete logoMap[targetTrimmed.toLowerCase()];
  try {
    localStorage.setItem(CUSTOM_COMPANY_LOGOS_STORAGE_KEY, JSON.stringify(logoMap));
  } catch {
    // ignore storage errors
  }

  try {
    const response = await axiosInstance.delete(
      getProductUrl(API_CONFIG.ENDPOINTS.PRODUCT.DELETE_COMPANY),
      { params: { CompanyName: targetTrimmed } }
    );
    return response.data;
  } catch {
    return null;
  }
}